import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowLeft, Search, Plus, Package, Edit, Trash2, Save, X,
  ChevronDown, ChevronUp, RefreshCw, AlertCircle, CheckCircle,
  Star, Boxes, FlaskConical, BarChart3, ShoppingCart, TrendingDown,
  AlertTriangle, CheckSquare, Upload, FileDown, FileText
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../utils/api";

/**
 * Inventory tab of the Purchasing hub — stock item CRUD against
 * /api/inventory/items.
 *
 * An "item" is a product master record (type, nominal code, generic
 * drug name, concentration, stock/par levels) with an embedded array of
 * supplier offers (each supplier row carries its own codes, pack size,
 * price, min/max order qty) — so one drug can be bought from several
 * suppliers and the UI compares them per line.
 *
 * Also owns the CSV bulk-import flow: downloadTemplate() builds the
 * template client-side and CSV_COLUMNS must stay in the same order as
 * the backend's CSV_ALL_COLS (routes/inventory.py) — they are coupled
 * by position, not by name mapping on the server.
 */
const ITEM_TYPES = ["Drug", "Consumable", "Equipment", "Fluid", "Food", "Other"];

// ── CSV template columns (must match backend CSV_ALL_COLS) ────────────────────
var CSV_COLUMNS = [
  "item_type", "nominal_code", "product_code", "description",
  "generic_name", "conc_qty", "conc_unit", "conc_format",
  "qty_in_stock", "par_level", "reorder_point",
  "supplier_name", "supplier_code", "outer_quantity", "price",
  "selling_units", "cost_per_selling_unit", "min_order_qty",
  "max_order_qty", "is_default", "supplier_notes"
];

var CSV_EXAMPLE_ROWS = [
  [
    "Drug", "5000", "AMOX-250", "Amoxicillin 250mg Capsules",
    "amoxicillin", "250", "mg", "capsule", "50", "20", "30",
    "VetPharm Ltd", "VP-AMX250", "100", "45.00",
    "capsule", "0.45", "1", "10", "true", ""
  ],
  [
    "", "", "AMOX-250", "Amoxicillin 250mg Capsules",
    "", "", "", "", "", "", "",
    "MediSupply Co", "MS-4421", "500", "200.00",
    "capsule", "0.40", "2", "20", "false", "Bulk discount available"
  ],
  [
    "Consumable", "", "GLOVES-L", "Nitrile Gloves Large",
    "", "", "", "box", "10", "5", "8",
    "SurgSupplies", "SS-GL-L", "10", "55.00",
    "box", "5.50", "1", "5", "true", ""
  ]
];

function downloadTemplate() {
  var rows = [CSV_COLUMNS.join(",")];
  CSV_EXAMPLE_ROWS.forEach(function(r) {
    rows.push(r.map(function(v) {
      var s = String(v);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    }).join(","));
  });
  var blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = "inventory_import_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcCost(price, outerQty, sellingUnits) {
  var p = parseFloat(price);
  var o = parseFloat(outerQty);
  var s = parseFloat(sellingUnits);
  if (!isNaN(p) && !isNaN(o) && o > 0 && !isNaN(s) && s > 0) {
    return (p / o / s).toFixed(4);
  }
  if (!isNaN(p) && !isNaN(o) && o > 0) {
    return (p / o).toFixed(4);
  }
  return "";
}

function emptyChild() {
  return {
    id: "",
    supplier_id: "",
    supplier_name: "",
    supplier_code: "",
    trade_name: "",
    outer_quantity: "",
    outer_quantity_type: "",
    price: "",
    selling_units: "",
    cost_per_selling_unit: "",
    min_order_qty: "",
    max_order_qty: "",
    is_default: false,
    notes: ""
  };
}

function emptyItem() {
  return {
    item_type: "",
    nominal_code: "",
    product_code: "",
    description: "",
    generic_name: "",
    conc_qty: "",
    conc_unit: "",
    conc_format: "",
    unit_type: "",
    unit_has_subunits: false,
    unit_subqty: "",
    unit_subtype: "",
    unit_dispensing: "",
    conc_strength: "",
    conc_strength_unit: "",
    medicine_type: "",
    suppliers: []
  };
}

var CONC_UNITS = ["ug", "mg", "g", "kg", "IU", "mmol", "%", "other"];
var CONC_FORMATS = ["ml", "L", "tablet", "capsule", "sachet", "vial", "ampoule", "patch", "tube", "bottle", "inhaler", "other"];

var UNIT_TYPES = ["Bottle", "Box", "Packet", "Tray", "Tub", "Tube", "Bag", "Vial", "Ampoule", "Canister", "Inhaler", "Sachet", "Other"];

var UNIT_SUBTYPE_OPTIONS = {
  Bottle:   ["ml", "L", "g", "tablet", "capsule", "other"],
  Vial:     ["ml", "IU", "other"],
  Ampoule:  ["ml", "other"],
  Box:      ["tablet", "capsule", "sachet", "ampoule", "vial", "other"],
  Packet:   ["tablet", "capsule", "sachet", "g", "other"],
  Tray:     ["tablet", "capsule", "vial", "ampoule", "other"],
  Tub:      ["tablet", "capsule", "g", "ml", "other"],
  Tube:     ["g", "ml", "other"],
  Bag:      ["ml", "L", "g", "other"],
  Canister: ["ml", "g", "dose", "other"],
  Inhaler:  ["dose", "mcg", "other"],
  Sachet:   ["g", "ml", "other"],
  Other:    ["ml", "L", "mg", "g", "tablet", "capsule", "other"],
};

function unitSummary(f) {
  if (!f.unit_type) return "";
  var type = f.unit_type.toLowerCase();
  if (f.unit_has_subunits && f.unit_subqty && f.unit_subtype) {
    return "1 " + type + " contains " + f.unit_subqty + " " + f.unit_subtype;
  }
  if (f.unit_dispensing) {
    return "Dispensing unit: " + f.unit_dispensing;
  }
  return "1 " + type;
}

function UnitSetup({ form, setField, inp }) {
  var subtypeOptions = (UNIT_SUBTYPE_OPTIONS[form.unit_type] || ["other"]);
  var dispensingOptions = form.unit_has_subunits && form.unit_subtype
    ? [form.unit_type, form.unit_subtype]
    : (form.unit_type ? [form.unit_type] : []);
  var summary = unitSummary(form);

  return (
    <div className="space-y-4">
      {/* Row 1: unit type */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Unit Type</label>
          <select value={form.unit_type || ""}
            onChange={function(e) {
              setField("unit_type", e.target.value);
              setField("unit_has_subunits", false);
              setField("unit_subqty", "");
              setField("unit_subtype", "");
              setField("unit_dispensing", "");
            }}
            className={inp}>
            <option value="">— select —</option>
            {UNIT_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
          </select>
        </div>
        {form.unit_type && (
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.unit_has_subunits}
                onChange={function(e) {
                  setField("unit_has_subunits", e.target.checked);
                  if (!e.target.checked) {
                    setField("unit_subqty", "");
                    setField("unit_subtype", "");
                    setField("unit_dispensing", "");
                  }
                }}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Has sub-units?
            </label>
          </div>
        )}
      </div>

      {/* Row 2: subunit config — shown only when has_subunits */}
      {form.unit_type && form.unit_has_subunits && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 pl-4 border-l-2 border-purple-100">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Number of sub-units</label>
            <input
              type="number" min="0" step="any"
              value={form.unit_subqty || ""}
              onChange={function(e) { setField("unit_subqty", e.target.value); }}
              placeholder={"e.g. 5"}
              className={inp}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sub-unit type</label>
            <select value={form.unit_subtype || ""}
              onChange={function(e) {
                setField("unit_subtype", e.target.value);
                setField("unit_dispensing", "");
              }}
              className={inp}>
              <option value="">— select —</option>
              {subtypeOptions.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dispensing unit</label>
            <select value={form.unit_dispensing || ""}
              onChange={function(e) { setField("unit_dispensing", e.target.value); }}
              className={inp}>
              <option value="">— select —</option>
              {dispensingOptions.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
            </select>
          </div>
        </div>
      )}

      {/* Row 3: dispensing unit when no subunits */}
      {form.unit_type && !form.unit_has_subunits && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dispensing unit</label>
            <select value={form.unit_dispensing || ""}
              onChange={function(e) { setField("unit_dispensing", e.target.value); }}
              className={inp}>
              <option value="">— select —</option>
              {dispensingOptions.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
            </select>
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg px-4 py-2.5">
          <FlaskConical className="h-4 w-4 text-purple-400 shrink-0" />
          <span className="text-xs text-purple-800 font-medium">{summary}</span>
        </div>
      )}
    </div>
  );
}

function concLabel(item) {
  var parts = [];
  if (item.conc_qty) parts.push(item.conc_qty + (item.conc_unit ? item.conc_unit : ""));
  if (item.conc_format) parts.push(item.conc_format);
  return parts.join(" / ");
}

var STRENGTH_UNITS = ["g", "mg", "mcg", "IU", "mmol", "%"];

var MEDICINE_TYPES = [
  {
    value: "POM-V",
    label: "POM-V",
    description: "Prescription Only Medicine — Veterinarian. Must be prescribed by a vet following clinical assessment of an animal under their care."
  },
  {
    value: "CD",
    label: "Controlled Drug (CD)",
    description: "Controlled Drug listed under the Misuse of Drugs Regulations 2001. Subject to Schedule 1–5 restrictions depending on therapeutic usefulness and misuse potential."
  },
  {
    value: "POM-VPS",
    label: "POM-VPS",
    description: "Prescription Only Medicine — Veterinarian, Pharmacist or SQP. Can be prescribed by any Registered Qualified Person without clinical assessment, provided sufficient information about the animal is known."
  },
  {
    value: "NFA-VPS",
    label: "NFA-VPS",
    description: "Non-Food Animal — Veterinarian, Pharmacist or SQP. Can be supplied by an RQP without a prescription, provided supply requirements are met."
  },
  {
    value: "AVM-GSL",
    label: "AVM-GSL",
    description: "Authorised Veterinary Medicine — General Sales List. No legal restrictions on retail supply, but a responsible approach to supply is expected."
  },
];

function ConcStrength({ form, setField, inp }) {
  var subunit = form.unit_subtype || form.unit_type || "";
  var subunitLabel = subunit ? subunit.toLowerCase() : "unit";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Strength
          </label>
          <input
            type="number" min="0" step="any"
            value={form.conc_strength || ""}
            onChange={function(e) { setField("conc_strength", e.target.value); }}
            placeholder="e.g. 250"
            className={inp}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Unit
          </label>
          <select value={form.conc_strength_unit || ""}
            onChange={function(e) { setField("conc_strength_unit", e.target.value); }}
            className={inp}>
            <option value="">— select —</option>
            {STRENGTH_UNITS.map(function(u) { return <option key={u} value={u}>{u}</option>; })}
            {subunit && <option value={"%" + subunit}>% {subunit}</option>}
          </select>
        </div>
        {subunit && (
          <div className="flex items-end pb-2">
            <span className="text-sm text-gray-500">per {subunitLabel}</span>
          </div>
        )}
      </div>
      {form.conc_strength && form.conc_strength_unit && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5">
          <FlaskConical className="h-4 w-4 text-blue-400 shrink-0" />
          <span className="text-xs text-blue-800 font-medium">
            {form.conc_strength}{form.conc_strength_unit}{subunit ? " per " + subunitLabel : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function MedicineTypeSelect({ form, setField, inp }) {
  var selected = MEDICINE_TYPES.find(function(m) { return m.value === form.medicine_type; });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Medicine Classification</label>
          <select value={form.medicine_type || ""}
            onChange={function(e) { setField("medicine_type", e.target.value); }}
            className={inp}>
            <option value="">— none / not applicable —</option>
            {MEDICINE_TYPES.map(function(m) { return <option key={m.value} value={m.value}>{m.label}</option>; })}
          </select>
        </div>
      </div>
      {selected && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-xs text-amber-900 leading-relaxed">
          <span className="font-semibold">{selected.label}: </span>{selected.description}
        </div>
      )}
    </div>
  );
}

// ── Supplier selector hook ────────────────────────────────────────────────────

function useSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  useEffect(function() {
    apiFetch("/api/purchasing/suppliers")
      .then(function(r) { return r.ok ? r.json() : { items: [] }; })
      .then(function(d) { setSuppliers(d.items || []); })
      .catch(function() {});
  }, []);
  return suppliers;
}

// ── Supplier child row ────────────────────────────────────────────────────────

const ChildRow = ({ child, idx, suppliers, onUpdate, onRemove, onSetDefault, isOnly }) => {
  var cost = calcCost(child.price, child.outer_quantity, child.selling_units);

  var update = function(field, val) {
    var updated = Object.assign({}, child, { [field]: val });
    if (field === "price" || field === "outer_quantity" || field === "selling_units") {
      updated.cost_per_selling_unit = calcCost(
        field === "price" ? val : child.price,
        field === "outer_quantity" ? val : child.outer_quantity,
        field === "selling_units" ? val : child.selling_units
      );
    }
    onUpdate(idx, updated);
  };

  var inp = "w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none";

  return (
    <tr className="border-b border-gray-100 hover:bg-purple-50/30">
      {/* Default marker */}
      <td className="px-2 py-1.5 text-center w-10">
        <button
          type="button"
          title="Set as default supplier"
          onClick={function() { onSetDefault(idx); }}
          className={"rounded-full p-0.5 " + (child.is_default ? "text-yellow-500" : "text-gray-300 hover:text-yellow-400")}>
          <Star className="h-4 w-4" fill={child.is_default ? "currentColor" : "none"} />
        </button>
      </td>
      {/* Supplier name */}
      <td className="px-2 py-1.5 min-w-[140px]">
        <select value={child.supplier_id || ""}
          onChange={function(e) {
            var sup = suppliers.find(function(s) { return s.id === e.target.value; });
            var updated = Object.assign({}, child, {
              supplier_id: e.target.value,
              supplier_name: sup ? sup.name : ""
            });
            onUpdate(idx, updated);
          }}
          className={inp}>
          <option value="">— select supplier —</option>
          {suppliers.map(function(s) {
            return <option key={s.id} value={s.id}>{s.name}</option>;
          })}
        </select>
      </td>
      {/* Supplier code */}
      <td className="px-2 py-1.5 w-28">
        <input value={child.supplier_code || ""} onChange={function(e) { update("supplier_code", e.target.value); }}
          placeholder="code" className={inp} />
      </td>
      {/* Trade name */}
      <td className="px-2 py-1.5 min-w-[120px]">
        <input value={child.trade_name || ""} onChange={function(e) { update("trade_name", e.target.value); }}
          placeholder="trade/brand name" className={inp} />
      </td>
      {/* Outer qty */}
      <td className="px-2 py-1.5 w-20">
        <input value={child.outer_quantity || ""} onChange={function(e) { update("outer_quantity", e.target.value); }}
          placeholder="e.g. 100" className={inp} type="number" min="0" step="any" />
      </td>
      {/* Price */}
      <td className="px-2 py-1.5 w-24">
        <input value={child.price || ""} onChange={function(e) { update("price", e.target.value); }}
          placeholder="£0.00" className={inp} type="number" min="0" step="0.0001" />
      </td>
      {/* Cost per selling unit – calculated, read-only */}
      <td className="px-2 py-1.5 w-28 text-center">
        <span className={"text-xs font-mono " + (cost ? "text-green-700 font-semibold" : "text-gray-400")}>
          {cost ? "£" + cost : "—"}
        </span>
      </td>
      {/* Min order */}
      <td className="px-2 py-1.5 w-20">
        <input value={child.min_order_qty || ""} onChange={function(e) { update("min_order_qty", e.target.value); }}
          placeholder="min" className={inp} type="number" min="0" />
      </td>
      {/* Max order */}
      <td className="px-2 py-1.5 w-20">
        <input value={child.max_order_qty || ""} onChange={function(e) { update("max_order_qty", e.target.value); }}
          placeholder="max" className={inp} type="number" min="0" />
      </td>
      {/* Notes */}
      <td className="px-2 py-1.5 min-w-[120px]">
        <input value={child.notes || ""} onChange={function(e) { update("notes", e.target.value); }}
          placeholder="notes" className={inp} />
      </td>
      {/* Remove */}
      <td className="px-2 py-1.5 w-8 text-center">
        <button type="button" onClick={function() { onRemove(idx); }}
          className="text-gray-300 hover:text-red-500">
          <X className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
};

// ── Item form (create / edit) ─────────────────────────────────────────────────

const ItemForm = ({ initial, onSaved, onCancel }) => {
  const suppliers = useSuppliers();
  const [form, setForm] = useState(function() {
    return initial ? JSON.parse(JSON.stringify(initial)) : emptyItem();
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  var isEdit = !!(initial && initial.id);

  var setField = function(field, val) {
    setForm(function(prev) { return Object.assign({}, prev, { [field]: val }); });
  };

  var addChild = function() {
    setForm(function(prev) {
      return Object.assign({}, prev, {
        suppliers: prev.suppliers.concat([emptyChild()])
      });
    });
  };

  var updateChild = function(idx, updated) {
    setForm(function(prev) {
      var arr = prev.suppliers.slice();
      arr[idx] = updated;
      return Object.assign({}, prev, { suppliers: arr });
    });
  };

  var removeChild = function(idx) {
    setForm(function(prev) {
      var arr = prev.suppliers.filter(function(_, i) { return i !== idx; });
      return Object.assign({}, prev, { suppliers: arr });
    });
  };

  var setDefault = function(idx) {
    setForm(function(prev) {
      var arr = prev.suppliers.map(function(c, i) {
        return Object.assign({}, c, { is_default: i === idx });
      });
      return Object.assign({}, prev, { suppliers: arr });
    });
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!form.description.trim()) { setError("Description is required."); return; }
    setSaving(true);
    setError(null);
    try {
      var url = isEdit
        ? "/api/inventory/items/" + initial.id
        : "/api/inventory/items";
      var method = isEdit ? "PATCH" : "POST";
      var res = await apiFetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      onSaved(data);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  var inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header fields */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-purple-600" />
          {isEdit ? "Edit Inventory Item" : "New Inventory Item"}
        </h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select value={form.item_type || ""} onChange={function(e) { setField("item_type", e.target.value); }} className={inp}>
              <option value="">— select —</option>
              {ITEM_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nominal Code</label>
            <input value={form.nominal_code || ""} onChange={function(e) { setField("nominal_code", e.target.value); }}
              className={inp} placeholder="e.g. 5000" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Product Code</label>
            <input value={form.product_code || ""} onChange={function(e) { setField("product_code", e.target.value); }}
              className={inp} placeholder="internal code" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-red-500">*</span></label>
            <input value={form.description || ""} onChange={function(e) { setField("description", e.target.value); }}
              className={inp} placeholder="product description" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Generic Name</label>
            <input value={form.generic_name || ""} onChange={function(e) { setField("generic_name", e.target.value); }}
              className={inp} placeholder="e.g. amoxicillin" />
          </div>
        </div>
      </div>

      {/* Unit Setup */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-purple-500" /> Unit Setup
        </h3>
        <UnitSetup form={form} setField={setField} inp={inp} />
      </div>

      {/* Concentration / Strength */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-blue-500" /> Concentration / Strength
        </h3>
        <ConcStrength form={form} setField={setField} inp={inp} />
      </div>

      {/* Medicine Type */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-amber-500" /> Medicine Classification
        </h3>
        <MedicineTypeSelect form={form} setField={setField} inp={inp} />
      </div>

      {/* Stock levels */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-purple-500" /> Stock Levels
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Qty in Stock</label>
            <input value={form.qty_in_stock ?? ""} onChange={function(e) { setField("qty_in_stock", e.target.value); }}
              className={inp} type="number" min="0" step="any" placeholder="0" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Par Level <span className="text-gray-400 font-normal">(min acceptable)</span></label>
            <input value={form.par_level ?? ""} onChange={function(e) { setField("par_level", e.target.value); }}
              className={inp} type="number" min="0" step="any" placeholder="0" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reorder Point <span className="text-gray-400 font-normal">(trigger PO)</span></label>
            <input value={form.reorder_point ?? ""} onChange={function(e) { setField("reorder_point", e.target.value); }}
              className={inp} type="number" min="0" step="any" placeholder="0" />
          </div>
        </div>
      </div>

      {/* Supplier children table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Boxes className="h-4 w-4 text-purple-500" />
            Supplier Options
            <span className="ml-1 text-xs text-gray-400 font-normal">(★ = default supplier)</span>
          </h3>
          <button type="button" onClick={addChild}
            className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 hover:bg-purple-50 rounded-lg px-3 py-1.5 font-medium">
            <Plus className="h-3.5 w-3.5" /> Add Supplier
          </button>
        </div>

        {form.suppliers.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">
            No suppliers added yet — click "Add Supplier" above
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-gray-500 font-medium">
                  <th className="px-2 py-2 w-10 text-center">Default</th>
                  <th className="px-2 py-2 text-left min-w-[140px]">Supplier</th>
                  <th className="px-2 py-2 text-left w-28">Supplier Code</th>
                  <th className="px-2 py-2 text-left min-w-[120px]">Trade Name</th>
                  <th className="px-2 py-2 text-left w-20">Outer Qty</th>
                  <th className="px-2 py-2 text-left w-24">Price (£)</th>
                  <th className="px-2 py-2 text-center w-28">Cost / Unit</th>
                  <th className="px-2 py-2 text-left w-20">Min Order</th>
                  <th className="px-2 py-2 text-left w-20">Max Order</th>
                  <th className="px-2 py-2 text-left min-w-[120px]">Notes</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.suppliers.map(function(child, idx) {
                  return (
                    <ChildRow
                      key={idx}
                      child={child}
                      idx={idx}
                      suppliers={suppliers}
                      onUpdate={updateChild}
                      onRemove={removeChild}
                      onSetDefault={setDefault}
                      isOnly={form.suppliers.length === 1}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isEdit ? "Save Changes" : "Create Item"}
        </button>
      </div>
    </form>
  );
};

// ── Expanded item detail ──────────────────────────────────────────────────────

const ItemDetail = ({ item, onEdit, onDelete }) => {
  var defaultSup = (item.suppliers || []).find(function(s) { return s.is_default; });
  return (
    <div className="bg-gray-50 border-t border-gray-100 px-5 py-4 space-y-3">
      {/* Meta grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-4">
        {[
          ["Type", item.item_type],
          ["Nominal Code", item.nominal_code],
          ["Product Code", item.product_code],
          ["Generic Name", item.generic_name],
          ["Concentration", concLabel(item) || null],
          ["Default Supplier", defaultSup ? defaultSup.supplier_name : "—"]
        ].map(function(pair) {
          return pair[1] ? (
            <div key={pair[0]}>
              <span className="text-gray-400 font-medium">{pair[0]}: </span>
              <span className="text-gray-700">{pair[1]}</span>
            </div>
          ) : null;
        })}
      </div>

      {/* Suppliers table */}
      {(item.suppliers || []).length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-gray-400 font-medium">
                <th className="py-2 px-3 text-left w-6"></th>
                <th className="py-2 px-3 text-left">Supplier</th>
                <th className="py-2 px-3 text-left">Code</th>
                <th className="py-2 px-3 text-left">Trade Name</th>
                <th className="py-2 px-3 text-right">Outer Qty</th>
                <th className="py-2 px-3 text-right">Price</th>
                <th className="py-2 px-3 text-right">Cost/Unit</th>
                <th className="py-2 px-3 text-right">Min</th>
                <th className="py-2 px-3 text-right">Max</th>
                <th className="py-2 px-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {item.suppliers.map(function(s, i) {
                return (
                  <tr key={i} className={"hover:bg-gray-50 " + (s.is_default ? "bg-yellow-50/40" : "")}>
                    <td className="py-1.5 px-3">
                      {s.is_default && <Star className="h-3.5 w-3.5 text-yellow-500" fill="currentColor" />}
                    </td>
                    <td className="py-1.5 px-3 font-medium text-gray-800">{s.supplier_name || "—"}</td>
                    <td className="py-1.5 px-3 font-mono text-gray-500">{s.supplier_code || "—"}</td>
                    <td className="py-1.5 px-3 text-gray-600">{s.trade_name || "—"}</td>
                    <td className="py-1.5 px-3 text-right text-gray-600">{s.outer_quantity || "—"}</td>
                    <td className="py-1.5 px-3 text-right text-gray-600">{s.price ? "£" + s.price : "—"}</td>
                    <td className="py-1.5 px-3 text-right font-semibold text-green-700">
                      {s.cost_per_selling_unit ? "£" + s.cost_per_selling_unit : "—"}
                    </td>
                    <td className="py-1.5 px-3 text-right text-gray-500">{s.min_order_qty || "—"}</td>
                    <td className="py-1.5 px-3 text-right text-gray-500">{s.max_order_qty || "—"}</td>
                    <td className="py-1.5 px-3 text-gray-500">{s.notes || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button onClick={onEdit}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium">
          <Edit className="h-3.5 w-3.5" /> Edit
        </button>
        <button onClick={function() {
          if (window.confirm("Delete \"" + item.description + "\"? This cannot be undone.")) onDelete();
        }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg font-medium">
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
      </div>
    </div>
  );
};

// ── Import CSV Modal ──────────────────────────────────────────────────────────

const ImportCSVModal = ({ onClose, onImported }) => {
  var [mode, setMode] = useState("products"); // "products" | "template"
  var [file, setFile] = useState(null);
  var [importing, setImporting] = useState(false);
  var [result, setResult] = useState(null);
  var [error, setError] = useState(null);
  var fileRef = useRef();

  var handleFile = function(e) {
    var f = e.target.files && e.target.files[0];
    if (f) { setFile(f); setResult(null); setError(null); }
  };

  var handleModeChange = function(m) {
    setMode(m);
    setFile(null);
    setResult(null);
    setError(null);
  };

  var handleImport = async function() {
    if (!file) return;
    setImporting(true);
    setError(null);
    setResult(null);
    try {
      var fd = new FormData();
      fd.append("file", file);
      var endpoint = mode === "products"
        ? "/api/inventory/items/import-products-csv"
        : "/api/inventory/items/import-csv";
      var res = await apiFetch(endpoint, { method: "POST", body: fd });
      var data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
      onImported();
    } catch(err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="h-5 w-5 text-purple-600" /> Import Inventory
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">

          {/* Mode toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            <button onClick={function() { handleModeChange("products"); }}
              className={"flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                (mode === "products" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              Products CSV (PMS export)
            </button>
            <button onClick={function() { handleModeChange("template"); }}
              className={"flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors " +
                (mode === "template" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
              VetFlow template
            </button>
          </div>

          {/* Mode-specific notes */}
          {mode === "products" ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 space-y-1">
              <p className="font-semibold">Products CSV format (from your PMS export):</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Expects the standard <strong>Products CSV export</strong> with columns like "Product Name", "Product Code", "Product Financial Product Group", etc.</li>
                <li>Items are matched by <strong>Product Code</strong> — existing codes are skipped (no overwrite)</li>
                <li>Supplier name and default supplier code are imported as supplier child records</li>
                <li>Concentration, prescription flag, notes and stock levels are all imported</li>
              </ul>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 space-y-1">
              <p className="font-semibold">VetFlow template format:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Each row = one item/supplier combination</li>
                <li>Multiple rows with the same <code>product_code</code> are grouped as supplier variants</li>
                <li>Existing items are <strong>updated</strong>; new items are <strong>created</strong></li>
                <li>Stock quantities only updated if CSV value is non-zero</li>
              </ul>
            </div>
          )}

          {/* Template download (only for template mode) */}
          {mode === "template" && (
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 w-full px-4 py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-purple-400 hover:text-purple-700 hover:bg-purple-50 transition-colors">
              <FileDown className="h-4 w-4" />
              Download template CSV (with example rows)
            </button>
          )}

          {/* File picker */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Select CSV file</label>
            <div
              onClick={function() { fileRef.current && fileRef.current.click(); }}
              className={"flex items-center gap-3 border-2 border-dashed rounded-xl p-5 cursor-pointer transition-colors " +
                (file ? "border-purple-400 bg-purple-50" : "border-gray-300 hover:border-purple-400 hover:bg-gray-50")}>
              <FileText className={"h-8 w-8 shrink-0 " + (file ? "text-purple-500" : "text-gray-300")} />
              <div>
                {file
                  ? <p className="text-sm font-medium text-purple-700">{file.name}</p>
                  : <p className="text-sm text-gray-500">Click to select a .csv file</p>}
                {file && <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>}
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
          </div>

          {/* Result */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 space-y-1">
              <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                <CheckCircle className="h-4 w-4 shrink-0" /> Import complete
              </div>
              <div className={"text-xs text-green-700 grid gap-2 mt-2 " + (mode === "products" ? "grid-cols-3" : "grid-cols-3")}>
                {mode === "products" ? (
                  <>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold text-green-600">{result.created}</div>
                      <div className="text-gray-500">Created</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold text-gray-400">{result.skipped}</div>
                      <div className="text-gray-500">Skipped</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold text-red-400">{result.total_errors}</div>
                      <div className="text-gray-500">Errors</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold">{result.rows_processed}</div>
                      <div className="text-gray-500">Rows read</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold text-green-600">{result.created}</div>
                      <div className="text-gray-500">Created</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                      <div className="text-xl font-bold text-blue-600">{result.updated}</div>
                      <div className="text-gray-500">Updated</div>
                    </div>
                  </>
                )}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5 max-h-32 overflow-y-auto">
                  <p className="font-semibold">Warnings / errors:</p>
                  {result.errors.map(function(e, i) {
                    return <p key={i}>{typeof e === "string" ? e : (e.row + ": " + e.error)}</p>;
                  })}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
            {!result && (
              <button onClick={handleImport} disabled={!file || importing}
                className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {importing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {importing ? "Importing…" : "Import"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Add to PO Modal ───────────────────────────────────────────────────────────

const AddToPOModal = ({ item, onClose, onAdded }) => {
  var defSup = (item.suppliers || []).find(function(s) { return s.is_default; }) || (item.suppliers || [])[0] || null;
  var [form, setForm] = useState({
    supplier_id: defSup ? (defSup.supplier_id || "") : "",
    supplier_name: defSup ? (defSup.supplier_name || "") : "",
    supplier_child_id: defSup ? (defSup.id || "") : "",
    supplier_code: defSup ? (defSup.supplier_code || "") : "",
    nominal_code: item.nominal_code || "",
    outer_quantity: defSup ? (defSup.outer_quantity || "") : "",
    outer_quantity_type: defSup ? (defSup.outer_quantity_type || "") : "",
    unit_price: defSup ? (defSup.price || "") : "",
    cost_per_selling_unit: defSup ? (defSup.cost_per_selling_unit || "") : "",
    qty_ordered: defSup ? (defSup.min_order_qty || "1") : "1",
  });
  var [existingPO, setExistingPO] = useState(undefined); // undefined=loading, null=none, obj=found
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);

  var inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none";

  // Look up existing draft PO for the selected supplier whenever supplier changes
  useEffect(function() {
    if (!form.supplier_name && !form.supplier_id) { setExistingPO(null); return; }
    setExistingPO(undefined);
    var params = new URLSearchParams();
    if (form.supplier_id) params.set("supplier_id", form.supplier_id);
    else params.set("supplier_name", form.supplier_name);
    apiFetch("/api/inventory/purchase-orders/draft-for-supplier?" + params.toString())
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setExistingPO(d || null); })
      .catch(function() { setExistingPO(null); });
  }, [form.supplier_id, form.supplier_name]);

  var handleSupplierChange = function(childId) {
    var child = (item.suppliers || []).find(function(s) { return s.id === childId; });
    if (!child) return;
    setForm(function(prev) {
      return Object.assign({}, prev, {
        supplier_id: child.supplier_id || "",
        supplier_name: child.supplier_name || "",
        supplier_child_id: child.id || "",
        supplier_code: child.supplier_code || "",
        nominal_code: form.nominal_code,
        outer_quantity: child.outer_quantity || "",
        outer_quantity_type: child.outer_quantity_type || "",
        unit_price: child.price || "",
        cost_per_selling_unit: child.cost_per_selling_unit || "",
        qty_ordered: child.min_order_qty || "1"
      });
    });
  };

  // ── Cheaper supplier hint ─────────────────────────────────────────────────
  // Compute cost-per-unit for every known supplier that has pricing data.
  // Returns { supplier, cpu } for the cheapest one that isn't the current selection.
  var cheaperSupplier = (function() {
    var suppliers = item.suppliers || [];
    if (suppliers.length < 2) return null;

    var cpuFor = function(s) {
      // Prefer stored cost_per_selling_unit, else derive from price / outer_quantity
      if (s.cost_per_selling_unit && parseFloat(s.cost_per_selling_unit) > 0)
        return parseFloat(s.cost_per_selling_unit);
      var p = parseFloat(s.price);
      var o = parseFloat(s.outer_quantity);
      if (p > 0 && o > 0) return p / o;
      return null;
    };

    var currentCpu = cpuFor({ price: form.unit_price, outer_quantity: form.outer_quantity, cost_per_selling_unit: form.cost_per_selling_unit });
    if (!currentCpu) return null; // can't compare without current price

    var best = null;
    suppliers.forEach(function(s) {
      if (s.id === form.supplier_child_id) return; // skip current
      var cpu = cpuFor(s);
      if (cpu === null) return;
      if (cpu < currentCpu) {
        if (!best || cpu < best.cpu) best = { supplier: s, cpu: cpu };
      }
    });
    return best;
  })();

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!form.supplier_name) { setError("Please select a supplier."); return; }
    if (!(parseFloat(form.qty_ordered) > 0)) { setError("Quantity must be greater than 0."); return; }
    setSaving(true);
    setError(null);
    try {
      var line = {
        inventory_item_id: item.id,
        inventory_item_description: item.description,
        inventory_item_size: concLabel(item),
        supplier_child_id: form.supplier_child_id === "__other__" ? "" : form.supplier_child_id,
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        supplier_code: form.supplier_code,
        nominal_code: form.nominal_code || "",
        qty_ordered: parseFloat(form.qty_ordered) || 0,
        unit_price: form.unit_price,
        outer_quantity: form.outer_quantity,
        outer_quantity_type: form.outer_quantity_type,
        cost_per_selling_unit: form.cost_per_selling_unit,
        qty_received: 0,
        received: false,
        received_at: ""
      };

      var data;
      if (existingPO) {
        // Add line to existing draft PO
        var res = await apiFetch("/api/inventory/purchase-orders/" + existingPO.id + "/add-line", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(line)
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to add to PO");
      } else {
        // Create new draft PO
        var res = await apiFetch("/api/inventory/purchase-orders", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            supplier_id: form.supplier_id,
            supplier_name: form.supplier_name,
            notes: "",
            lines: [line]
          })
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to create PO");
      }
      onAdded(data, !!existingPO);
    } catch(err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-purple-600" />
            Add to Purchase Order
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-purple-50 rounded-lg px-4 py-2.5 text-sm">
            <span className="font-medium text-purple-800">{item.description}</span>
            {concLabel(item) && <span className="text-purple-600 ml-2">{concLabel(item)}</span>}
          </div>

          {/* Supplier selector — always visible */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
            <select
              value={form.supplier_child_id || "__other__"}
              onChange={function(e) {
                if (e.target.value === "__other__") {
                  setForm(function(prev) {
                    return Object.assign({}, prev, {
                      supplier_child_id: "__other__",
                      supplier_id: "",
                      supplier_name: "",
                      supplier_code: "",
                      outer_quantity: "",
                      outer_quantity_type: "",
                      unit_price: "",
                      cost_per_selling_unit: "",
                      qty_ordered: "1"
                    });
                  });
                } else {
                  handleSupplierChange(e.target.value);
                }
              }}
              className={inp}>
              {(item.suppliers || []).map(function(s) {
                return (
                  <option key={s.id} value={s.id}>
                    {s.supplier_name}{s.is_default ? " ★" : ""}{s.supplier_code ? " · " + s.supplier_code : ""}{s.trade_name ? " (" + s.trade_name + ")" : ""}
                  </option>
                );
              })}
              <option value="__other__">Different supplier…</option>
            </select>
            {form.supplier_child_id === "__other__" && (
              <input
                value={form.supplier_name}
                onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { supplier_name: e.target.value }); }); }}
                className={inp + " mt-2"}
                placeholder="Supplier name"
              />
            )}
          </div>

          {/* Cheaper supplier advisory */}
          {cheaperSupplier && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800">
              <span className="mt-0.5 shrink-0 text-green-500">💡</span>
              <span>
                <strong>{cheaperSupplier.supplier.supplier_name}</strong> is cheaper at{" "}
                <strong>£{cheaperSupplier.cpu.toFixed(4)}/unit</strong>
                {cheaperSupplier.supplier.outer_quantity && cheaperSupplier.supplier.price
                  ? " (£" + parseFloat(cheaperSupplier.supplier.price).toFixed(2) + " per " +
                    (cheaperSupplier.supplier.outer_quantity_type || "outer") + " of " +
                    cheaperSupplier.supplier.outer_quantity + ")"
                  : ""}
                {cheaperSupplier.supplier.supplier_code ? " · code: " + cheaperSupplier.supplier.supplier_code : ""}.{" "}
                <button type="button"
                  onClick={function() { handleSupplierChange(cheaperSupplier.supplier.id); }}
                  className="underline font-semibold hover:text-green-900">
                  Switch to this supplier
                </button>
              </span>
            </div>
          )}

          {/* Existing draft PO banner */}
          {existingPO === undefined && form.supplier_name && (
            <div className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
              <RefreshCw className="h-3 w-3 animate-spin" /> Checking for open PO…
            </div>
          )}
          {existingPO && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
              <CheckCircle className="h-3.5 w-3.5 shrink-0 text-blue-500" />
              <span>Open draft PO found: <strong>{existingPO.po_number}</strong> — this line will be added to it.</span>
            </div>
          )}
          {existingPO === null && form.supplier_name && (
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600">
              <ShoppingCart className="h-3.5 w-3.5 shrink-0" />
              No open draft PO for {form.supplier_name} — a new draft PO will be created.
            </div>
          )}

          {/* Outer pack info — read-only display */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Outer Pack Details</p>
            <div className="flex items-center gap-6">
              <div>
                <span className="text-xs text-gray-500">Units per pack</span>
                <p className="text-sm font-semibold text-gray-800">{form.outer_quantity || <span className="text-gray-400 font-normal">—</span>}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Pack type</span>
                <p className="text-sm font-semibold text-gray-800">{form.outer_quantity_type || <span className="text-gray-400 font-normal">—</span>}</p>
              </div>
              {form.unit_price && (
                <div>
                  <span className="text-xs text-gray-500">Price per pack</span>
                  <p className="text-sm font-semibold text-gray-800">£{parseFloat(form.unit_price).toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Order quantity — framed around outers */}
          <div className="border border-gray-200 rounded-lg px-4 py-3 space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              No. of {form.outer_quantity_type ? form.outer_quantity_type + "s" : "Outers"} to Order
            </label>
            <input value={form.qty_ordered} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { qty_ordered: e.target.value }); }); }}
              className={inp} type="number" min="0" step="any" required />
            {/* Total units hint */}
            {form.qty_ordered && form.outer_quantity && parseFloat(form.qty_ordered) > 0 && parseFloat(form.outer_quantity) > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                = <span className="font-semibold text-gray-700">
                  {(parseFloat(form.qty_ordered) * parseFloat(form.outer_quantity)).toLocaleString()} units
                </span> total
                {form.outer_quantity_type && (
                  <span className="text-gray-400"> ({form.qty_ordered} × {form.outer_quantity} per {form.outer_quantity_type})</span>
                )}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Supplier Code</label>
              <input value={form.supplier_code} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { supplier_code: e.target.value }); }); }}
                className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nominal Code</label>
              <input value={form.nominal_code} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, { nominal_code: e.target.value }); }); }}
                className={inp} placeholder="e.g. 5000" />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || existingPO === undefined}
              className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
              {existingPO ? "Add to " + existingPO.po_number : "Create Draft PO"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Stock Management tab ──────────────────────────────────────────────────────

const StockManagement = ({ navigateTo }) => {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(false);
  var [loadError, setLoadError] = useState(null);
  var [search, setSearch] = useState("");
  var [filterStatus, setFilterStatus] = useState(""); // "" | "low" | "reorder" | "ok"
  var [saving, setSaving] = useState({});
  var [edits, setEdits] = useState({});
  var [poItem, setPoItem] = useState(null);
  var [successMsg, setSuccessMsg] = useState(null);
  // poQtyMap[item_id] = { draft: number, sent: number }
  var [poQtyMap, setPoQtyMap] = useState({});

  var load = useCallback(async function() {
    setLoading(true);
    setLoadError(null);
    try {
      var params = new URLSearchParams({ skip: 0, limit: 500 });
      if (search) params.set("search", search);
      var [itemsRes, posRes] = await Promise.all([
        apiFetch("/api/inventory/items?" + params.toString()),
        apiFetch("/api/inventory/purchase-orders?skip=0&limit=500")
      ]);
      var itemsData = await itemsRes.json();
      var posData = await posRes.json();
      setItems(itemsData.items || []);
      // Build qty lookup: sum qty_ordered per inventory_item_id per status group
      var map = {};
      var draftStatuses = ["draft", "submitted", "approved"];
      var sentStatuses  = ["sent", "partial"];
      (posData.items || []).forEach(function(po) {
        var group = draftStatuses.includes(po.status) ? "draft"
                  : sentStatuses.includes(po.status)  ? "sent"
                  : null;
        if (!group) return;
        (po.lines || []).forEach(function(line) {
          var id = line.inventory_item_id;
          if (!id) return;
          if (!map[id]) map[id] = { draft: 0, sent: 0 };
          map[id][group] += (parseFloat(line.qty_ordered) || 0) * (parseFloat(line.outer_quantity) || 1);
        });
      });
      setPoQtyMap(map);
    } catch(err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(function() { load(); }, [load]);

  var stockStatus = function(item) {
    var qty = parseFloat(item.qty_in_stock) || 0;
    var par = parseFloat(item.par_level) || 0;
    var reorder = parseFloat(item.reorder_point) || 0;
    if (par > 0 && qty < par) return "low";
    if (reorder > 0 && qty <= reorder) return "reorder";
    return "ok";
  };

  var displayed = items.filter(function(item) {
    if (!filterStatus) return true;
    return stockStatus(item) === filterStatus;
  });

  var getEdit = function(id, field, fallback) {
    return (edits[id] && edits[id][field] !== undefined) ? edits[id][field] : fallback;
  };

  var setEdit = function(id, field, val) {
    setEdits(function(prev) {
      return Object.assign({}, prev, { [id]: Object.assign({}, prev[id] || {}, { [field]: val }) });
    });
  };

  var saveStock = async function(item) {
    var patch = edits[item.id] || {};
    if (Object.keys(patch).length === 0) return;
    setSaving(function(p) { return Object.assign({}, p, { [item.id]: true }); });
    try {
      var res = await apiFetch("/api/inventory/items/" + item.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
      });
      if (!res.ok) throw new Error("Save failed");
      setEdits(function(prev) { var n = Object.assign({}, prev); delete n[item.id]; return n; });
      load();
    } catch(err) {
      alert("Error saving: " + err.message);
    } finally {
      setSaving(function(p) { var n = Object.assign({}, p); delete n[item.id]; return n; });
    }
  };

  var statusBadge = function(status) {
    if (status === "low") return { cls: "bg-red-100 text-red-700", icon: <AlertTriangle className="h-3 w-3" />, label: "Below Par" };
    if (status === "reorder") return { cls: "bg-amber-100 text-amber-700", icon: <TrendingDown className="h-3 w-3" />, label: "Reorder" };
    return { cls: "bg-green-100 text-green-700", icon: <CheckSquare className="h-3 w-3" />, label: "OK" };
  };

  var statsLow = items.filter(function(i) { return stockStatus(i) === "low"; }).length;
  var statsReorder = items.filter(function(i) { return stockStatus(i) === "reorder"; }).length;

  var inp = "border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none w-20 text-center";

  return (
    <div className="space-y-4">
      {poItem && (
        <AddToPOModal
          item={poItem}
          onClose={function() { setPoItem(null); }}
          onAdded={function(po, wasExisting) {
            setPoItem(null);
            setSuccessMsg((wasExisting ? "Line added to " : "Draft PO created: ") + po.po_number + " — " + po.supplier_name);
            setTimeout(function() { setSuccessMsg(null); }, 5000);
          }}
        />
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 rounded-lg"><BarChart3 className="h-5 w-5 text-blue-600" /></div>
          <div><div className="text-2xl font-bold text-gray-900">{items.length}</div><div className="text-xs text-gray-500">Total Items</div></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 cursor-pointer hover:border-amber-300"
          onClick={function() { setFilterStatus(filterStatus === "reorder" ? "" : "reorder"); }}>
          <div className="p-2.5 bg-amber-100 rounded-lg"><TrendingDown className="h-5 w-5 text-amber-600" /></div>
          <div><div className="text-2xl font-bold text-amber-700">{statsReorder}</div><div className="text-xs text-gray-500">Need Reorder</div></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 cursor-pointer hover:border-red-300"
          onClick={function() { setFilterStatus(filterStatus === "low" ? "" : "low"); }}>
          <div className="p-2.5 bg-red-100 rounded-lg"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
          <div><div className="text-2xl font-bold text-red-700">{statsLow}</div><div className="text-xs text-gray-500">Below Par</div></div>
        </div>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm">
          <CheckCircle className="h-4 w-4 shrink-0" />{successMsg}
          {navigateTo && (
            <button onClick={function() { navigateTo("purchase-orders"); }}
              className="ml-2 underline text-green-800 hover:text-green-900">View Purchase Orders</button>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Search items…"
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
        </div>
        <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-500">
          <option value="">All items</option>
          <option value="reorder">Needs reorder</option>
          <option value="low">Below par</option>
          <option value="ok">OK</option>
        </select>
        <button onClick={load}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className={"h-4 w-4 text-gray-500" + (loading ? " animate-spin" : "")} />
        </button>
      </div>

      {loadError && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />{loadError}
        </div>
      )}

      {displayed.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No items match this filter</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                <th className="px-4 py-3 text-left">Item</th>
                <th className="px-4 py-3 text-center w-24">Status</th>
                <th className="px-4 py-3 text-center w-28">In Stock</th>
                <th className="px-4 py-3 text-center w-24">Par Level</th>
                <th className="px-4 py-3 text-center w-24">Reorder At</th>
                <th className="px-4 py-3 text-center w-24">On Draft PO</th>
                <th className="px-4 py-3 text-center w-24">On Sent PO</th>
                <th className="px-4 py-3 text-center w-20">Save</th>
                <th className="px-4 py-3 text-center w-28">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map(function(item) {
                var status = stockStatus(item);
                var badge = statusBadge(status);
                var hasEdits = !!(edits[item.id] && Object.keys(edits[item.id]).length > 0);
                var defSup = (item.suppliers || []).find(function(s) { return s.is_default; }) || (item.suppliers || [])[0];
                var poQty = poQtyMap[item.id] || { draft: 0, sent: 0 };
                return (
                  <tr key={item.id} className={"hover:bg-gray-50/60" + (status === "low" ? " bg-red-50/30" : status === "reorder" ? " bg-amber-50/30" : "")}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-sm">{item.description}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {item.item_type && <span className="mr-2">{item.item_type}</span>}
                        {concLabel(item) && <span>{concLabel(item)}</span>}
                        {defSup && <span className="ml-2 text-gray-400">· {defSup.supplier_name}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={"inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium " + badge.cls}>
                        {badge.icon}{badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number" min="0" step="any"
                        value={getEdit(item.id, "qty_in_stock", item.qty_in_stock ?? 0)}
                        onChange={function(e) { setEdit(item.id, "qty_in_stock", parseFloat(e.target.value) || 0); }}
                        className={inp + " " + (status === "low" ? "border-red-300 text-red-700 font-bold" : "")} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number" min="0" step="any"
                        value={getEdit(item.id, "par_level", item.par_level ?? 0)}
                        onChange={function(e) { setEdit(item.id, "par_level", parseFloat(e.target.value) || 0); }}
                        className={inp} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number" min="0" step="any"
                        value={getEdit(item.id, "reorder_point", item.reorder_point ?? 0)}
                        onChange={function(e) { setEdit(item.id, "reorder_point", parseFloat(e.target.value) || 0); }}
                        className={inp + " " + (status === "reorder" ? "border-amber-300 text-amber-700 font-bold" : "")} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {poQty.draft > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          {poQty.draft % 1 === 0 ? poQty.draft : poQty.draft.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {poQty.sent > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">
                          {poQty.sent % 1 === 0 ? poQty.sent : poQty.sent.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {hasEdits && (
                        <button onClick={function() { saveStock(item); }}
                          disabled={saving[item.id]}
                          className="flex items-center gap-1 mx-auto text-xs px-2.5 py-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg font-medium">
                          {saving[item.id] ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(item.suppliers || []).length > 0 ? (
                        <button onClick={function() { setPoItem(item); }}
                          className={"flex items-center gap-1 mx-auto text-xs px-2.5 py-1 rounded-lg font-medium border " +
                            (status !== "ok" ? "bg-purple-600 text-white hover:bg-purple-700 border-purple-600" : "text-purple-600 border-purple-200 hover:bg-purple-50")}>
                          <ShoppingCart className="h-3 w-3" /> Add to PO
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No suppliers</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main Inventory page ───────────────────────────────────────────────────────

const Inventory = ({ navigateTo }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [typeOptions, setTypeOptions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("catalogue"); // "catalogue" | "stock"
  const [view, setView] = useState("list"); // "list" | "form"
  const [editItem, setEditItem] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkType, setBulkType] = useState("");
  const [bulkNominal, setBulkNominal] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);

  const load = useCallback(async function() {
    setSelectedIds([]);
    setLoading(true);
    setLoadError(null);
    try {
      var params = new URLSearchParams({ skip: 0, limit: 200 });
      if (search) params.set("search", search);
      if (filterType) params.set("item_type", filterType);
      var res = await apiFetch("/api/inventory/items?" + params.toString());
      var data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch(err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterType]);

  // Load type options for filter dropdown
  useEffect(function() {
    apiFetch("/api/inventory/types")
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setTypeOptions(d); })
      .catch(function() {});
  }, [items]);

  useEffect(function() { load(); }, [load]);

  var handleSaved = function(saved) {
    setView("list");
    setEditItem(null);
    setSuccessMsg((editItem ? "Updated" : "Created") + ": " + saved.description);
    load();
    setTimeout(function() { setSuccessMsg(null); }, 4000);
  };

  var handleEdit = function(item) {
    setEditItem(item);
    setView("form");
    setExpanded(null);
  };

  var handleDelete = async function(item) {
    await apiFetch("/api/inventory/items/" + item.id, { method: "DELETE" });
    setExpanded(null);
    load();
  };

  var handleBulkUpdate = async function() {
    if (!selectedIds.length) return;
    var payload = { ids: selectedIds };
    if (bulkType) payload.item_type = bulkType;
    if (bulkNominal.trim()) payload.nominal_code = bulkNominal.trim();
    if (!payload.item_type && !payload.nominal_code) return;
    setBulkApplying(true);
    try {
      var res = await apiFetch("/api/inventory/items/bulk-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      var data = await res.json();
      setSuccessMsg("Updated " + data.updated + " item" + (data.updated !== 1 ? "s" : ""));
      setSelectedIds([]);
      setBulkType("");
      setBulkNominal("");
      load();
      setTimeout(function() { setSuccessMsg(null); }, 4000);
    } catch(err) {
      // silent
    } finally {
      setBulkApplying(false);
    }
  };

  var toggleExpanded = function(id) {
    setExpanded(expanded === id ? null : id);
  };

  // ── TYPE badge colour ──
  var typeBadge = function(t) {
    var map = {
      Drug: "bg-blue-100 text-blue-700",
      Consumable: "bg-gray-100 text-gray-600",
      Equipment: "bg-orange-100 text-orange-700",
      Fluid: "bg-cyan-100 text-cyan-700",
      Food: "bg-green-100 text-green-700",
      Other: "bg-purple-100 text-purple-700"
    };
    return map[t] || "bg-gray-100 text-gray-600";
  };

  // ── Render ──
  if (view === "form" && tab === "catalogue") {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={function() { setView("list"); setEditItem(null); }}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Inventory
          </button>
          <ItemForm
            initial={editItem}
            onSaved={handleSaved}
            onCancel={function() { setView("list"); setEditItem(null); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {navigateTo && (
              <button onClick={function() { navigateTo("home"); }}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-500">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-600 rounded-xl">
                <FlaskConical className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
                <p className="text-sm text-gray-500">{total} item{total !== 1 ? "s" : ""}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {navigateTo && (
              <button onClick={function() { navigateTo("purchase-orders"); }}
                className="flex items-center gap-2 px-4 py-2 border border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl text-sm font-medium">
                <ShoppingCart className="h-4 w-4" /> Purchase Orders
              </button>
            )}
            {tab === "catalogue" && (
              <>
                <button onClick={downloadTemplate}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium" title="Download CSV template">
                  <FileDown className="h-4 w-4" /> Template
                </button>
                <button onClick={function() { setShowImport(true); }}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium">
                  <Upload className="h-4 w-4" /> Import CSV
                </button>
                <button
                  onClick={function() { setEditItem(null); setView("form"); }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium">
                  <Plus className="h-4 w-4" /> New Item
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          <button onClick={function() { setTab("catalogue"); }}
            className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " +
              (tab === "catalogue" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
            <Package className="h-4 w-4" /> Catalogue
          </button>
          <button onClick={function() { setTab("stock"); }}
            className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " +
              (tab === "stock" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700")}>
            <BarChart3 className="h-4 w-4" /> Stock Management
          </button>
        </div>

        {/* Success */}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" />{successMsg}
          </div>
        )}

        {/* Import modal */}
        {showImport && (
          <ImportCSVModal
            onClose={function() { setShowImport(false); }}
            onImported={function() { load(); }}
          />
        )}

        {/* Stock tab */}
        {tab === "stock" && <StockManagement navigateTo={navigateTo} />}

        {tab === "catalogue" && (
          <React.Fragment>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[160px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={function(e) { setSearch(e.target.value); }}
                  placeholder="Search description, code, generic name…"
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
              </div>
              <select value={filterType} onChange={function(e) { setFilterType(e.target.value); }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white">
                <option value="">All types</option>
                {typeOptions.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
              </select>
              <button onClick={load}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" title="Refresh">
                <RefreshCw className={"h-4 w-4 text-gray-500" + (loading ? " animate-spin" : "")} />
              </button>
            </div>

            {/* Bulk action bar */}
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-purple-800">{selectedIds.length} item{selectedIds.length !== 1 ? "s" : ""} selected</span>
                <button onClick={function() { setSelectedIds([]); }} className="text-xs text-purple-500 hover:text-purple-700 underline">Clear</button>
                <div className="flex-1" />
                <select value={bulkType} onChange={function(e) { setBulkType(e.target.value); }}
                  className="text-sm border border-purple-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-purple-500 focus:outline-none">
                  <option value="">— Type: no change —</option>
                  {ITEM_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
                </select>
                <input value={bulkNominal} onChange={function(e) { setBulkNominal(e.target.value); }}
                  placeholder="Nominal code (blank = no change)"
                  className="text-sm border border-purple-300 rounded-lg px-3 py-1.5 w-52 focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                <button onClick={handleBulkUpdate}
                  disabled={bulkApplying || (!bulkType && !bulkNominal.trim())}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                  {bulkApplying ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckSquare className="h-3.5 w-3.5" />}
                  Apply to {selectedIds.length}
                </button>
              </div>
            )}

            {/* Error */}
            {loadError && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />{loadError}
              </div>
            )}

            {/* List */}
            {items.length === 0 && !loading && !loadError ? (
              <div className="bg-white rounded-xl border border-gray-200 p-14 text-center text-gray-400">
                <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No inventory items found</p>
                <p className="text-sm mt-1">Click "New Item" to add your first product</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                      <th className="px-3 py-3 w-10">
                        <input type="checkbox"
                          checked={items.length > 0 && selectedIds.length === items.length}
                          onChange={function(e) {
                            setSelectedIds(e.target.checked ? items.map(function(i) { return i.id; }) : []);
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer" />
                      </th>
                      <th className="px-4 py-3 text-left w-6"></th>
                      <th className="px-4 py-3 text-left">Description</th>
                      <th className="px-4 py-3 text-left">Generic Name</th>
                      <th className="px-4 py-3 text-left w-28">Type</th>
                      <th className="px-4 py-3 text-left w-28">Product Code</th>
                      <th className="px-4 py-3 text-left w-28">Concentration</th>
                      <th className="px-4 py-3 text-left w-20">Nominal</th>
                      <th className="px-4 py-3 text-center w-20">Suppliers</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map(function(item) {
                      var isOpen = expanded === item.id;
                      var defSup = (item.suppliers || []).find(function(s) { return s.is_default; });
                      return (
                        <React.Fragment key={item.id}>
                          <tr
                            className={"cursor-pointer hover:bg-purple-50/40 transition-colors" + (isOpen ? " bg-purple-50/30" : "")}
                            onClick={function() { toggleExpanded(item.id); }}>
                            <td className="px-3 py-3" onClick={function(e) { e.stopPropagation(); }}>
                              <input type="checkbox"
                                checked={selectedIds.includes(item.id)}
                                onChange={function(e) {
                                  setSelectedIds(e.target.checked
                                    ? selectedIds.concat(item.id)
                                    : selectedIds.filter(function(x) { return x !== item.id; }));
                                }}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer" />
                            </td>
                            <td className="px-4 py-3 text-gray-400">
                              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{item.description}</div>
                              {defSup && (
                                <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                  <Star className="h-3 w-3 text-yellow-400" fill="currentColor" />
                                  {defSup.supplier_name}
                                  {defSup.cost_per_selling_unit ? " · £" + defSup.cost_per_selling_unit + "/unit" : ""}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-600">{item.generic_name || "—"}</td>
                            <td className="px-4 py-3">
                              {item.item_type && (
                                <span className={"text-xs px-2 py-0.5 rounded-full font-medium " + typeBadge(item.item_type)}>
                                  {item.item_type}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.product_code || "—"}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{concLabel(item) || "—"}</td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.nominal_code || "—"}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 font-medium">
                                {(item.suppliers || []).length}
                              </span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={9} className="p-0">
                                <ItemDetail
                                  item={item}
                                  onEdit={function() { handleEdit(item); }}
                                  onDelete={function() { handleDelete(item); }}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default Inventory;
